/*
 * PokeSouq: Notify Me helpers (loaded deferred from layout/theme.liquid).
 *
 * 1. FairDrop sign-ups - only when the theme setting "Use FairDrop notify list" is on
 *    (sections/api-notification.liquid then renders data-ps-fairdrop="true", a consent
 *    tick, a honeypot and the product data). A document-level CAPTURE submit listener
 *    takes the popup's submit before the theme's own handler: preventDefault() AND
 *    stopImmediatePropagation() are both needed, because the theme handler ignores
 *    defaultPrevented and calls the native form.submit() anyway.
 *    Any status other than 200/400(known code)/429, a network error or the 6 s timeout
 *    falls back to today's path: form.requestSubmit(), let through once by a flag,
 *    which ends in the theme's native POST to /contact.
 *    The fallback hands over to the theme's handler, whose form.submit() is replaced by
 *    Shopify's hCaptcha wrapper once hCaptcha has bound to the form (a second or two after
 *    the email field gets focus): that path then runs the invisible captcha before the
 *    POST, exactly as today's switch-off path does.
 * 2. Quick view: the "Sold Out - Notify Me" link (snippets/pokesouq-notify-quickview.liquid)
 *    keeps ?variant= on the selected variant (on every variant change, and again on
 *    pointer/focus/click), and a sold-out quick-view form never submits (keyboard Enter
 *    included).
 * 3. The consent tick's "required" bubble shows our wording, not the browser's default.
 * 4. Safety net for /contact sign-ups (switch off, or the switch-on fallback): if the page
 *    still hasn't started to leave 15 s later - e.g. hCaptcha is blocked by an ad blocker, so
 *    its wrapper never posts - the popup says "Something went wrong. Please refresh and try
 *    again." instead of nothing. It waits while an hCaptcha picture challenge is open, and it
 *    is never armed on the FairDrop paths that answer for themselves (200 / 400 / 429).
 *
 * Contract: FairDrop final instructions v2, section 1.5.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://pokesouq-backend.vercel.app/api/restock/subscribe';
  var TIMEOUT_MS = 6000;
  var SAFETY_MS = 15000;        // /contact sign-up still on the page after this -> message
  var CHALLENGE_POLL_MS = 3000; // re-check interval while an hCaptcha challenge is open
  var MSG = {
    success: "You're on the list ✓ We'll email you the moment it's back.",
    invalid_email: 'Please check your email address.',
    consent_required: 'Please tick the box to get the email.',
    in_stock: "Good news, it's back in stock. Refresh the page.",
    invalid_variant: 'Something went wrong. Please refresh and try again.',
    rate_limited: 'Too many tries, please wait a minute and try again.',
    stalled: 'Something went wrong. Please refresh and try again.'
  };

  var passThrough = new WeakSet();   // forms allowed through once, to /contact

  function readMeta(form) {
    var el = form.querySelector('script[data-ps-notify-meta]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function setStatus(form, text, ok) {
    var el = form.querySelector('[data-ps-notify-status]');
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    el.classList.toggle('ps-notify-status--ok', !!ok);
  }

  function showSuccess(form) {
    var msg = form.querySelector('.product-notification__message');
    if (msg) {
      msg.textContent = '';
      var p = document.createElement('p');
      p.textContent = MSG.success;
      msg.appendChild(p);
    }
    var pswp = form.closest('.pswp');
    if (pswp) pswp.classList.add('pswp--success');   // theme CSS swaps the form for the message
    setStatus(form, MSG.success, true);               // visible even if that CSS doesn't apply
  }

  // 4. Safety net for a sign-up that heads for /contact but never leaves the page
  var leaving = false;
  var armedForms = new WeakSet();
  function onLeave() { leaving = true; }

  function challengeOpen() {   // hCaptcha's picture challenge, while the shopper solves it
    var frames = document.querySelectorAll('iframe[src*="hcaptcha"]');
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      if (String(f.getAttribute('src')).indexOf('frame=challenge') === -1) continue;
      var r = f.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && getComputedStyle(f).visibility !== 'hidden') return true;
    }
    return false;
  }

  function armSafetyNet(form) {
    if (armedForms.has(form)) return;
    armedForms.add(form);
    leaving = false;
    // listeners only while armed (a permanent beforeunload listener can cost bfcache)
    window.addEventListener('beforeunload', onLeave);
    window.addEventListener('pagehide', onLeave);
    function disarm() {
      armedForms.delete(form);
      window.removeEventListener('beforeunload', onLeave);
      window.removeEventListener('pagehide', onLeave);
    }
    function check() {
      if (leaving || !form.isConnected) { disarm(); return; }   // left, or popup closed
      if (challengeOpen()) { setTimeout(check, CHALLENGE_POLL_MS); return; }
      setStatus(form, MSG.stalled);
      disarm();
    }
    setTimeout(check, SAFETY_MS);
  }

  function fallback(form) {
    passThrough.add(form);
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  function buildBody(form, meta) {
    var email = form.querySelector('input[name="contact[email]"]');
    var consent = form.querySelector('[data-ps-consent]');
    var hp = form.querySelector('[data-ps-hp]');
    return {
      variant_id: String(meta.variant_id),
      product_id: String(meta.product_id),
      product_handle: meta.product_handle,
      product_title: meta.product_title,
      variant_title: meta.variant_title,
      email: email ? email.value.trim() : '',
      consent: !!(consent && consent.checked),
      source: 'web',
      locale: (window.Shopify && window.Shopify.locale) || 'en',
      customer_id: meta.customer_id ? String(meta.customer_id) : null,
      hp: hp ? hp.value : ''
    };
  }

  function send(form, meta) {
    var body = buildBody(form, meta);
    if (!body.consent) { setStatus(form, MSG.consent_required); return Promise.resolve('consent'); }

    var button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    var settled = false;
    var controller = typeof AbortController === 'function' ? new AbortController() : null;

    return new Promise(function (resolve) {
      function done(outcome) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (button) button.disabled = false;
        resolve(outcome);
      }
      var timer = setTimeout(function () {
        if (controller) controller.abort();
        if (!settled) { fallback(form); done('timeout'); }
      }, TIMEOUT_MS);

      fetch(ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        if (settled) return;
        if (res.status === 200) { showSuccess(form); done('success'); return; }
        if (res.status === 429) { setStatus(form, MSG.rate_limited); done('rate_limited'); return; }
        if (res.status === 400) {
          return res.json().then(function (j) {
            var code = j && j.error;
            if (code && MSG[code] && code !== 'success') { setStatus(form, MSG[code]); done(code); }
            else { fallback(form); done('fallback'); }
          }, function () { fallback(form); done('fallback'); });
        }
        fallback(form); done('fallback');
      }, function () {
        if (settled) return;
        fallback(form); done('fallback');
      });
    });
  }

  // 1. FairDrop takeover of the popup submit
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-notification-form]')) return;
    if (passThrough.has(form)) { passThrough.delete(form); armSafetyNet(form); return; }   // fallback: theme takes it to /contact
    if (form.getAttribute('data-ps-fairdrop') !== 'true') { armSafetyNet(form); return; }  // switch off: today's behaviour
    var meta = readMeta(form);
    if (!meta) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    send(form, meta);
  }, true);

  // 2a. Quick view: the notify link always carries the variant selected right now, so a
  //     left click, middle click, open-in-new-tab or copied link all get the right one
  function refreshLink(link) {
    var form = link.closest('form');
    var input = form && form.querySelector('[name="id"]');
    var base = link.getAttribute('data-ps-product-url');
    if (input && input.value && base) {
      var href = base + '?variant=' + encodeURIComponent(input.value);
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    }
  }
  function refreshAllLinks() {
    document.querySelectorAll('[data-ps-qv-notify]').forEach(refreshLink);
  }
  ['pointerdown', 'focusin', 'click', 'contextmenu'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var link = e.target.closest && e.target.closest('[data-ps-qv-notify]');
      if (link) refreshLink(link);
    }, true);
  });
  // product-info.js publishes this after each variant change (and sets the id input)
  function subscribeVariantChange() {
    var events = window.theme && window.theme.PUB_SUB_EVENTS;
    if (typeof window.subscribe !== 'function' || !events || !events.variantChange) return false;
    window.subscribe(events.variantChange, function () {
      refreshAllLinks();
      setTimeout(refreshAllLinks, 0);   // in case the id input is updated after the publish
    });
    return true;
  }
  if (!subscribeVariantChange()) document.addEventListener('DOMContentLoaded', subscribeVariantChange);

  // 2b. Quick view: a sold-out form never submits (Enter on the hidden button would
  //     otherwise run add-to-cart). The live sold-out state is on <product-form data-form-wrapper>.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('.popup-quick-view')) return;
    var wrapper = form.closest('[data-form-wrapper]');
    if (wrapper && wrapper.classList.contains('variant--soldout')) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  // 3. Consent: native "required" validation runs before any submit listener, so set our
  //    wording as the custom validity message when it fires; clear it once the box changes
  document.addEventListener('invalid', function (e) {
    var box = e.target;
    if (box && box.matches && box.matches('[data-ps-consent]') && !box.checked) box.setCustomValidity(MSG.consent_required);
  }, true);
  document.addEventListener('change', function (e) {
    var box = e.target;
    if (box && box.matches && box.matches('[data-ps-consent]')) box.setCustomValidity('');
  }, true);

  // exposed for the Node test harness only
  window.PokesouqNotify = { send: send, buildBody: buildBody, MSG: MSG, ENDPOINT: ENDPOINT, TIMEOUT_MS: TIMEOUT_MS, SAFETY_MS: SAFETY_MS };
})();
